<template>
    <div class="gpu-center-page">
        <main class="page-shell">
            <section class="hero">
                <div class="server-bg"></div>
                <div class="cube"></div>
                <h1>{{ getLabel('item.heroTitle', 'GPU 算力中心') }}</h1>
                <p>{{ getLabel('item.heroSubtitle', '高性能算力资源，驱动 AI 未来') }}</p>
                <div class="badges">
                    <div v-for="badge in heroBadges" :key="badge.title" class="badge">
                        <i>{{ badge.icon }}</i>
                        <div>
                            <b>{{ badge.title }}</b>
                            {{ badge.desc }}
                        </div>
                    </div>
                </div>
            </section>

            <section class="zone-grid">
                <button
                    v-for="zone in zoneCategories"
                    :key="zone.id"
                    type="button"
                    class="zone"
                    :class="{ active: activeCategory === Number(zone.id) }"
                    @click="changeCategory(zone.id)"
                >
                    <div class="zone-icon">{{ zone.icon }}</div>
                    <h3>{{ zone.title }}</h3>
                    <span>{{ zone.subtitle }}</span>
                </button>
            </section>

            <div class="section-head">
                <div class="section-title">
                    {{ activeZone.title }}
                    <small v-if="activeZone.subtitle">{{ activeZone.subtitle }}</small>
                </div>
            </div>

            <div v-if="!loading && !projectList.length" class="empty-wrap card-shell">
                <van-empty :description="getLabel('item.noProject', '暂无项目')" />
            </div>

            <section
                v-for="item in projectList"
                :key="item.id"
                class="card-shell product-card"
            >
                <div
                    class="gpu-img"
                    :style="{ backgroundImage: 'url(' + resolveItemImage(item) + ')' }"
                    @click="goDetail(item)"
                >
                    <em>Vast</em>
                </div>
               
                <h2 @click="goDetail(item)">{{ getItemTitle(item) }}</h2>

                <div class="metrics">
                    <div class="metric">
                        <i>INR</i>
                        <b>{{ formatMoney(item.min) }}</b>
                        {{ getLabel('item.minPurchase', '起购金额') }}
                    </div>
                    <div class="metric">
                        <i>∞</i>
                        <b>{{ item.day || 0 }}D</b>
                        {{ getLabel('item.contractCycle', '合约周期') }}
                    </div>
                    <div class="metric">
                        <i>◕</i>
                        <b>{{ formatPercent(item.percent) }}%</b>
                        {{ getLabel('item.progressLabel', '认购进度') }}
                    </div>
                </div>

                <div class="money-grid">
                    <div class="money-card">
                        {{ getLabel('item.bonusIncome', '收益加成') }}
                        <b class="green">+{{ formatCurrency(computeVipBonus(item)) }}</b>
                    </div>
                    <div class="money-card">
                        {{ getLabel('item.estimatedDaily', '日收益（预估）') }}
                        <b class="white">{{ formatCurrency(computeDailyIncome(item)) }}</b>
                    </div>
                    <div class="money-card">
                        {{ getLabel('item.estimatedTotal', '总收益（预估）') }}
                        <b class="purple">{{ formatCurrency(computeTotalIncome(item)) }}</b>
                    </div>
                </div>

                <div class="income-strip">
                    <div class="income-pill">
                        <span class="pill-icon">▦</span>
                        {{ getLabel('item.dailyEstimate', '预估日收益:') }}
                        <b>{{ formatPercent(item.rate) }}%</b>
                    </div>
                    <div class="income-pill">
                        <span class="pill-icon">◈</span>
                        {{ getLabel('item.dailyReturnRule', '每日返息·到期返本') }}
                    </div>
                    <div class="income-pill limit-text">
                        <span class="pill-icon">⌂</span>
                        {{ getLabel('item.limitBuy', '限购:') }}
                        <b>{{ getLimitText(item) }}</b>
                    </div>
                </div>

                <div class="progress-row">
                    <div class="progress">
                        <i :style="{ width: formatPercent(item.percent) + '%' }"></i>
                    </div>
                    <div class="percent">{{ formatPercent(item.percent) }}%</div>
                </div>

                <div class="buy-wrap">
                    <button
                        type="button"
                        class="buy-btn"
                        :class="{ disabled: Number(item.percent) >= 100 }"
                        :disabled="Number(item.percent) >= 100"
                        @click="openBuyModal(item)"
                    >
                        {{ Number(item.percent) >= 100 ? getLabel('item.soldOut', '已售罄') : getLabel('item.buyNow', '立即认购') }}
                    </button>
                </div>
            </section>

            <section class="card-shell service-row">
                <div v-for="service in serviceItems" :key="service.title" class="service-item">
                    <i>{{ service.icon }}</i>
                    <b>{{ service.title }}</b>
                    {{ service.desc }}
                </div>
            </section>
        </main>

        <nav class="bottom-nav">
            <router-link to="/index" class="nav-item">
                <van-icon name="home-o" />
                <span>{{ $t('home.index') }}</span>
            </router-link>
            <router-link to="/item" class="nav-item active">
                <van-icon name="chart-trending-o" />
                <span>{{ getLabel('item.navSubscription', '算力认购') }}</span>
            </router-link>
            <router-link to="/user" class="nav-item">
                <van-icon name="contact" />
                <span>{{ $t('home.my') }}</span>
            </router-link>
        </nav>

        <button
            type="button"
            class="service-float"
            :aria-label="getLabel('user.onlineService', '在线客服')"
            @click="openKefu"
        >
            <van-icon name="service-o" />
        </button>

        <van-popup
            v-model="showBuyModal"
            position="bottom"
            class="agreement-popup"
            :overlay-style="{ background: 'rgba(2, 6, 18, 0.88)' }"
        >
            <div class="agreement">
                <!-- 标题栏 -->
                <div class="agreement-header">
                    <h2>{{ getLabel('item.agreementTitle', 'Commercial Cooperation Agreement') }}</h2>
                    <button type="button" class="agreement-close" :aria-label="getLabel('item.closeAgreement', 'Close')" @click="closeBuyModal">
                        <van-icon name="cross" />
                    </button>
                </div>

                <!-- 可滚动内容 -->
                <div class="agreement-body">
                    <!-- 基础信息 -->
                    <section class="agree-section">
                        <h3 class="agree-section-title">
                            <van-icon name="orders-o" />
                            {{ getLabel('item.infoBasic', 'Basic Information') }}
                        </h3>
                        <div class="info-grid">
                            <div class="info-cell">
                                <label>{{ getLabel('item.productName', 'Product Name') }}</label>
                                <b class="product-name">{{ productTitle }}</b>
                            </div>
                            <div class="info-cell">
                                <label>{{ getLabel('item.quantity', 'Quantity') }}</label>
                                <b>1</b>
                            </div>
                            <div class="info-cell">
                                <label>{{ getLabel('item.unitPrice', 'Unit Price') }}</label>
                                <b class="accent-green">{{ unitPrice }} INR</b>
                            </div>
                            <div class="info-cell">
                                <label>{{ getLabel('item.totalAmount', 'Total Amount') }}</label>
                                <b class="accent-green">{{ totalAmount }} INR</b>
                            </div>
                            <div class="info-cell">
                                <label>{{ getLabel('item.benefit', 'Profit') }}</label>
                                <b class="accent-gold">{{ dailyBenefit }} INR</b>
                            </div>
                            <div class="info-cell">
                                <label>{{ getLabel('item.cycleDays', 'Cycle Days') }}</label>
                                <b class="accent-blue">{{ cycleDays }} {{ getLabel('item.daysShort', 'Days') }}</b>
                            </div>
                        </div>
                    </section>

                    <!-- 合同条款（可滚动） -->
                    <section class="agree-section">
                        <h3 class="agree-section-title">
                            <van-icon name="info-o" />
                            {{ getLabel('item.contractTerms', 'Contract Terms') }}
                        </h3>
                        <div class="terms-scroll">
                           
                            <div class="terms-text" v-html="termsHtml"></div>
                        </div>
                    </section>

                    <!-- 签名信息（折叠） -->
                    <section class="agree-section">
                        <h3 class="agree-section-title signature-toggle" @click="showSignature = !showSignature">
                            <van-icon name="contact" />
                            {{ getLabel('item.signatureInfo', 'Signature Information') }}
                            <van-icon :name="showSignature ? 'arrow-up' : 'arrow-down'" class="chev" />
                        </h3>
                        <div v-show="showSignature" class="signature-body">
                            <div class="signature-row">
                                <span>{{ getLabel('item.signerName', 'Signer') }}</span>
                                <b>{{ getLabel('item.signerPlaceholder', 'Registered user') }}</b>
                            </div>
                            <div class="signature-row">
                                <span>{{ getLabel('item.signDate', 'Signature date') }}</span>
                                <b>{{ todayLabel }}</b>
                            </div>
                        </div>
                    </section>
                </div>

                <!-- 底部操作 -->
                <div class="agreement-footer">
                    <button type="button" class="accept-btn" :disabled="submitLoading" @click="submitBuy">
                        {{ submitLoading ? getLabel('utils.loading', 'Processing...') : getLabel('item.acceptBuy', 'Accept and Buy') }}
                    </button>
                    <p class="footer-note">{{ getLabel('item.agreementNote', 'By clicking above, you accept the terms of this agreement') }}</p>
                </div>
            </div>
        </van-popup>
    </div>
</template>

<script>
import Vue from "vue";
import Fetch from "../../utils/fetch";
import { Empty, Icon, Popup, Toast } from "vant";

Vue.use(Empty);
Vue.use(Icon);
Vue.use(Popup);
Vue.use(Toast);

export default {
    name: "item",
    data() {
        return {
            lang: this.$i18n.locale || "zh_cn",
            loading: false,
            data: {},
            projectList: [],
            activeCategory: 0,
            showBuyModal: false,
            currentItem: {},
            submitLoading: false,
            showSignature: false,
            appOriginalStyles: null,
            bodyOriginalBackground: "",
            zoneCategories: [],
            zoneIconPool: ["◇", "↗", "☁", "♕", "◈", "⬢", "✦", "⌘"],
        };
    },
    computed: {
        heroBadges() {
            return [
                { icon: "♢", title: this.getLabel("item.badgeTitle1", "稳定高效"), desc: this.getLabel("item.badgeDesc1", "99.9% 可用率") },
                { icon: "♢", title: this.getLabel("item.badgeTitle2", "安全可靠"), desc: this.getLabel("item.badgeDesc2", "多重安全保障") },
                { icon: "⌘", title: this.getLabel("item.badgeTitle3", "弹性扩展"), desc: this.getLabel("item.badgeDesc3", "灵活配置资源") },
            ];
        },
        serviceItems() {
            return [
                { icon: "⌂", title: this.getLabel("item.serviceTitle1", "极速交付"), desc: this.getLabel("item.serviceDesc1", "快速部署上线") },
                { icon: "⌬", title: this.getLabel("item.serviceTitle2", "稳定运行"), desc: this.getLabel("item.serviceDesc2", "99.9% 可用率") },
                { icon: "♢", title: this.getLabel("item.serviceTitle3", "数据安全"), desc: this.getLabel("item.serviceDesc3", "多重加密防护") },
                { icon: "♣", title: this.getLabel("item.serviceTitle4", "专业服务"), desc: this.getLabel("item.serviceDesc4", "7×24 小时支持") },
            ];
        },
        activeZone() {
            return this.zoneCategories.find((zone) => Number(zone.id) === Number(this.activeCategory)) || {
                title: this.getLabel("item.defaultProject", "GPU 项目"),
                subtitle: "",
            };
        },
        currentMoney() {
            return Number(this.currentItem.min || 0);
        },
        productTitle() {
            return this.getItemTitle(this.currentItem);
        },
        unitPrice() {
            return this.formatMoney(this.currentItem.min);
        },
        totalAmount() {
            return this.formatMoney(this.currentItem.min * 1);
        },
        dailyBenefit() {
            return this.formatMoney(this.computeDailyIncome(this.currentItem));
        },
        cycleDays() {
            return Number(this.currentItem.day || 0);
        },
        todayLabel() {
            const d = new Date();
            const y = d.getFullYear();
            const m = String(d.getMonth() + 1).padStart(2, "0");
            const day = String(d.getDate()).padStart(2, "0");
            return y + "-" + m + "-" + day;
        },
        termsHtml() {
            return (
                "<p>Welcome to the computing power rental service for mining and distributed computing " +
                "(hereinafter, \"this Service\") provided by CUTTING-EDGEAITECHNOLOGY (hereinafter, \"CUTTING\" or \"we\"). " +
                "This contract (hereinafter, \"this Contract\") consists of two parts: the Service Conditions and the " +
                "Lease Agreement, which constitute a legally binding agreement between you (hereinafter, " +
                "\"Party B\") and CUTTING (hereinafter, \"Party A\"). Please read all the conditions of this Contract " +
                "and, before using this Service or confirming the purchase of a rental product, confirm that you have " +
                "read, understood and accept to be bound by the conditions of this Contract.</p>" +
                "<p><b>Part One: Service Conditions</b></p>" +
                "<p><b>1. Acceptance of Conditions</b></p>" +
                "<p>1.1 By accessing or using the CUTTING platform (including the website, the application and related " +
                "services) and confirming the purchase of a product, you agree to be bound by these conditions. " +
                "If you do not agree with any of them, do not use the Service.</p>" +
                "<p>1.2 CUTTING reserves the right to modify or update this Contract at any time. The " +
                "modifications will take effect upon publication on the platform.</p>" +
                "<p><b>2. About the Rental Service</b></p>" +
                "<p>2.1 You rent computing power for a defined period. Profits are credited daily and the " +
                "principal is returned at the end of the cycle.</p>" +
                "<p>2.2 It is prohibited to use the Service for illegal activities or activities that violate applicable regulations.</p>"
            );
        },
    },
    created() {
        this.$parent.footer("item", false);
    },
    mounted() {
        this.applyFullScreenLayout();
        this.bootstrapPage();
    },
    beforeDestroy() {
        this.restoreFullScreenLayout();
    },
    methods: {
        getLabel(key, defaultText) {
            return this.$te(key) ? this.$t(key) : defaultText;
        },
        applyFullScreenLayout() {
            const app = document.getElementById("app");
            if (!app) {
                return;
            }
            this.appOriginalStyles = {
                maxWidth: app.style.maxWidth,
                width: app.style.width,
                margin: app.style.margin,
                backgroundColor: app.style.backgroundColor,
                paddingBottom: app.style.paddingBottom,
            };
            this.bodyOriginalBackground = document.body.style.background;
            app.style.maxWidth = "100%";
            app.style.width = "100%";
            app.style.margin = "0";
            app.style.backgroundColor = "#020611";
            app.style.paddingBottom = "0";
            document.body.style.background = "#020611";
        },
        restoreFullScreenLayout() {
            const app = document.getElementById("app");
            if (app && this.appOriginalStyles) {
                app.style.maxWidth = this.appOriginalStyles.maxWidth || "";
                app.style.width = this.appOriginalStyles.width || "";
                app.style.margin = this.appOriginalStyles.margin || "";
                app.style.backgroundColor = this.appOriginalStyles.backgroundColor || "";
                app.style.paddingBottom = this.appOriginalStyles.paddingBottom || "";
            }
            document.body.style.background = this.bodyOriginalBackground || "";
        },
        async bootstrapPage() {
            const queryCategory = Number(this.$route.query.category_id || this.$route.query.class || this.$route.query.type || 0);
            await this.loadCategories(queryCategory);
        },
        getCategoryIcon(index) {
            return this.zoneIconPool[index % this.zoneIconPool.length];
        },
        async loadCategories(preferredCategory) {
            const res = await Fetch("/index/item_class");
            const list = Array.isArray(res.data.list) ? res.data.list : [];
            this.zoneCategories = list.map((item, index) => ({
                ...item,
                id: Number(item.id),
                icon: this.getCategoryIcon(index),
                title: item.title || item[this.lang] || item.zh_cn || item.en_us || this.getLabel("item.categoryFallback", "项目分类"),
                subtitle: item.en_us || item.title || item.zh_cn || "",
            }));

            if (!this.zoneCategories.length) {
                this.activeCategory = 0;
                this.projectList = [];
                return;
            }

            const hasPreferredCategory = this.zoneCategories.some((zone) => Number(zone.id) === Number(preferredCategory));
            this.activeCategory = hasPreferredCategory ? Number(preferredCategory) : Number(this.zoneCategories[0].id);
            await this.start(this.activeCategory);
        },
        async start(categoryId) {
            this.loading = true;
            try {
                const res = await Fetch("/index/item_search", {
                    title: "",
                    class: categoryId,
                });
                this.data = res.data || {};
                this.projectList = Array.isArray(res.data.list) ? res.data.list : [];

                if (this.projectList.length) {
                    this.projectList.forEach((item, index) => {
                        Fetch("/index/item_detail", { id: item.id }).then((detailRes) => {
                            const detail = detailRes.data || {};
                            if (detail.img) {
                                this.$set(this.projectList[index], "img", detail.img);
                            }
                            if (detail.cover) {
                                this.$set(this.projectList[index], "cover", detail.cover);
                            }
                            if (detail.pic) {
                                this.$set(this.projectList[index], "pic", detail.pic);
                            }
                            if (detail.title && !this.projectList[index].title) {
                                this.$set(this.projectList[index], "title", detail.title);
                            }
                            if (typeof detail.num !== "undefined") {
                                this.$set(this.projectList[index], "num", detail.num);
                            }
                        }).catch(() => {});
                    });
                }
            } finally {
                this.loading = false;
            }
        },
        changeCategory(categoryId) {
            const nextCategoryId = Number(categoryId);
            if (this.activeCategory === nextCategoryId) {
                return;
            }
            this.activeCategory = nextCategoryId;
            this.$router.replace({ path: "/item", query: { category_id: nextCategoryId } });
            this.start(nextCategoryId);
        },
        getItemTitle(item) {
            if (!item) {
                return "";
            }
            return item[this.lang] || item.title || item.name || this.getLabel("item.defaultProject", "GPU 项目");
        },
        resolveItemImage(item) {
            return item.img || item.pic || item.cover || "https://6584545.it.com/uploads/20260718/2f1afd34468753d8a604941a18b35f56.jpg";
        },
        formatMoney(value) {
            return Number(value || 0).toFixed(2);
        },
        formatCurrency(value) {
            return "INR" + Number(value || 0).toFixed(2);
        },
        formatPercent(value) {
            return Number(value || 0).toFixed(1).replace(/\.0$/, "");
        },
        computeDailyIncome(item) {
            return Number(item.min || 0) * Number(item.rate || 0) / 100;
        },
        computeVipBonus(item) {
            return this.computeDailyIncome(item) * 0.1;
        },
        computeTotalIncome(item) {
            return (this.computeDailyIncome(item) + this.computeVipBonus(item)) * Number(item.day || 0);
        },
        computeReturnAmount(item) {
            return Number(item.min || 0) + this.computeTotalIncome(item);
        },
        getLimitText(item) {
            const limit = Number(item && item.num);
            if (!limit) {
                return this.getLabel("item.unlimited", "不限");
            }
            return "x" + limit;
        },
        goDetail(item) {
            this.$router.push("/item/" + item.id);
        },
        openBuyModal(item) {
            if (Number(item.percent) >= 100) {
                return;
            }
            this.currentItem = item;
            this.showBuyModal = true;
        },
        closeBuyModal() {
            this.showBuyModal = false;
            this.submitLoading = false;
            this.showSignature = false;
        },
        async submitBuy() {
            if (!this.currentItem.id) {
                return;
            }
            this.submitLoading = true;
            try {
                await Fetch("/index/item_apply", {
                    id: this.currentItem.id,
                    money: this.currentMoney,
                });
                this.closeBuyModal();
                this.$router.push("/order");
            } catch (error) {
                this.submitLoading = false;
            }
        },
        openKefu() {
            this.$router.push("/kefu");
        },
    },
};
</script>

<style lang="less" scoped>
.gpu-center-page {
    min-height: 100vh;
    background: #020611;
    color: #fff;
}

.page-shell {
    width: 100%;
    max-width: 430px;
    min-height: 100vh;
    margin: 0 auto;
    padding: 22px 16px 96px;
    background:
        radial-gradient(circle at 75% 5%, rgba(20, 82, 255, 0.3), transparent 28%),
        linear-gradient(180deg, #040816 0%, #020611 100%);
    position: relative;
    box-sizing: border-box;
    overflow: hidden;
}

.hero {
    position: relative;
    overflow: hidden;
    margin: 0 -16px 18px;
    padding: 30px 16px 30px;
}

.server-bg {
    position: absolute;
    right: 0;
    top: 0;
    width: 64%;
    height: 255px;
    background:
        linear-gradient(90deg, transparent, rgba(7, 18, 51, 0.25)),
        radial-gradient(circle at 62% 55%, rgba(127, 45, 255, 0.7), transparent 24%),
        radial-gradient(circle at 70% 30%, rgba(0, 123, 255, 0.38), transparent 42%);
    filter: saturate(1.15);
}

.server-bg::before {
    content: "";
    position: absolute;
    inset: 0;
    background:
        repeating-linear-gradient(90deg, rgba(28, 84, 230, 0.2) 0 2px, transparent 2px 42px),
        repeating-linear-gradient(0deg, rgba(0, 129, 255, 0.35) 0 4px, transparent 4px 20px);
    opacity: 0.35;
    clip-path: polygon(18% 0, 100% 0, 100% 82%, 44% 100%, 0 70%);
}

.cube {
    position: absolute;
    right: 50px;
    width: 135px;
    height: 82px;
    border: 2px solid rgba(77, 127, 255, 0.9);
    border-radius: 12px;
    background: linear-gradient(135deg, rgba(63, 43, 255, 0.75), rgba(0, 210, 255, 0.28));
    box-shadow: 0 0 30px rgba(79, 37, 255, 0.9), 0 0 60px rgba(0, 121, 255, 0.5);
    transform: perspective(450px) rotateX(58deg) rotateZ(-8deg);
}

.cube::before {
    content: "";
    position: absolute;
    left: 18px;
    right: 18px;
    top: 18px;
    height: 36px;
    border: 1px solid rgba(0, 238, 255, 0.55);
    box-shadow: inset 0 0 20px rgba(0, 210, 255, 0.45);
}

.hero h1,
.hero p,
.badges {
    position: relative;
    z-index: 2;
}

.hero h1 {
    margin: 0 0 10px;
    font-size: 34px;
    line-height: 1.1;
    font-weight: 900;
    letter-spacing: -1px;
}

.hero p {
    margin: 0 0 38px;
    color: #c8cedd;
    font-size: 15px;
}

.badges {
    display: flex;
    gap: 20px;
}

.badge {
    display: flex;
    align-items: center;
    gap: 7px;
    color: #a9b0c6;
    font-size: 10px;
}

.badge b {
    display: block;
    margin-bottom: 2px;
    color: #fff;
    font-size: 12px;
}

.badge i {
    width: 27px;
    height: 27px;
    border: 1px solid rgba(121, 75, 255, 0.3);
    border-radius: 50%;
    background: rgba(80, 43, 166, 0.28);
    color: #8d50ff;
    display: grid;
    place-items: center;
    font-style: normal;
}

.zone-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 6px;
    margin-bottom: 27px;
}

.zone {
    min-height: 118px;
    padding: 14px 6px 12px;
    border: 1px solid rgba(45, 82, 164, 0.32);
    border-radius: 12px;
    background: linear-gradient(160deg, rgba(12, 27, 62, 0.88), rgba(7, 12, 29, 0.88));
    box-shadow: inset 0 0 24px rgba(25, 73, 255, 0.07);
    color: #fff;
    text-align: center;
    overflow: hidden;
    cursor: pointer;
}

.zone.active {
    border-color: rgba(169, 60, 255, 0.62);
    box-shadow: inset 0 0 23px rgba(128, 49, 255, 0.22);
}

.zone-icon {
    height: 36px;
    color: #8f48ff;
    font-size: 33px;
    line-height: 23px;
    text-shadow: 0 0 14px rgba(146, 68, 255, 0.8);
}

.zone:nth-child(2) .zone-icon {
    color: #167eff;
}

.zone:nth-child(3) .zone-icon {
    color: #00d8ff;
}

.zone:nth-child(4) .zone-icon {
    color: #ffbd43;
}

.zone h3 {
    min-height: 30px;
    margin: 0 0 4px;
    font-size: 13px;
    line-height: 1.18;
    font-weight: 800;
    white-space: normal;
    word-break: break-word;
    overflow-wrap: anywhere;
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
}

.zone span {
    color: #9da5bd;
    font-size: 9px;
    line-height: 1.22;
    white-space: normal;
    word-break: break-word;
    overflow-wrap: anywhere;
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
}

.section-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
}

.section-title {
    font-size: 22px;
    font-weight: 900;
}

.section-title small {
    margin-left: 8px;
    padding: 3px 7px;
    border-radius: 6px;
    background: rgba(116, 50, 255, 0.16);
    color: #a45cff;
    font-size: 12px;
}

.card-shell {
    border: 1px solid rgba(54, 82, 176, 0.32);
    border-radius: 14px;
    background: linear-gradient(135deg, rgba(12, 24, 54, 0.96), rgba(5, 10, 26, 0.94));
    box-shadow: inset 0 0 38px rgba(26, 65, 255, 0.1);
    overflow: hidden;
}

.empty-wrap {
    margin-bottom: 14px;
    padding: 30px 10px;
}

.product-card {
    --product-image-width: clamp(124px, 35%, 146px);
    --product-image-gap: 10px;
    position: relative;
    min-height: 300px;
    padding: 20px 14px 18px;
    margin-bottom: 14px;
}

.gpu-img {
    position: absolute;
    right: 18px;
    top: 25px;
    z-index: 1;
    width: var(--product-image-width);
    aspect-ratio: 2.05 / 1;
    border-radius: 14px;
    background:
        linear-gradient(135deg, #1b1e29, #394059 48%, #080a10);
    background-position: center;
    background-repeat: no-repeat;
    background-size: cover;
    box-shadow: 0 0 20px rgba(113, 55, 255, 0.62);
    transform: skewX(-12deg);
    cursor: pointer;
}

.gpu-img em {
    position: absolute;
    left: 28px;
    top: 31px;
    color: #d6d9e6;
    font-size: 14px;
    font-style: normal;
    transform: skewX(12deg);
}

.tag,
.product-card h2,
.metrics,
.money-grid,
.income-strip,
.progress-row,
.buy-wrap {
    position: relative;
    z-index: 2;
}

.tag {
    display: inline-block;
    margin-bottom: 16px;
    padding: 4px 8px;
    border-radius: 7px;
    background: rgba(114, 48, 255, 0.25);
    color: #bf92ff;
    font-size: 12px;
}

.product-card h2 {
    max-width: calc(100% - var(--product-image-width) - var(--product-image-gap));
    margin: 0 0 18px;
    font-size: 22px;
    line-height: 1.18;
    letter-spacing: 0.2px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    cursor: pointer;
}

.metrics {
    width: 58%;
    margin: 35px 0 24px;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
}

.metric {
    min-height: 64px;
    border-right: 1px solid rgba(70, 82, 132, 0.25);
    color: #a1aabd;
    font-size: 11px;
    line-height: 1.45;
    text-align: center;
    white-space: nowrap;
}

.metric:last-child {
    border-right: 0;
}

.metric b {
    display: block;
    min-height: 26px;
    margin: 4px 0 8px;
    color: #fff;
    font-size: 15px;
    line-height: 1.2;
}

.metric i {
    display: grid;
    height: 22px;
    margin-bottom: 2px;
    color: #934eff;
    font-size: 21px;
    font-style: normal;
    align-content: center;
}

.money-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
    margin-bottom: 19px;
}

.money-card {
    min-height: 67px;
    padding: 10px 8px;
    border: 1px solid rgba(58, 78, 144, 0.38);
    border-radius: 9px;
    background: rgba(9, 18, 42, 0.76);
    color: #aab3c8;
    font-size: 11px;
}

.money-card b {
    display: block;
    margin-top: 5px;
    font-size: 20px;
}

.green {
    color: #17e49a;
}

.white {
    color: #fff;
}

.purple {
    color: #8c45ff;
}

.income-strip {
    display: flex;
    gap: 8px;
    margin: 0 0 17px;
}

.income-pill {
    flex: 1 1 auto;
    min-width: 0;
    height: 42px;
    padding: 0 5px;
    border: 1px solid rgba(143, 72, 255, 0.86);
    border-radius: 10px;
    background: rgba(8, 15, 38, 0.72);
    box-shadow: 0 0 10px rgba(143, 72, 255, 0.16), inset 0 0 18px rgba(93, 50, 255, 0.08);
    color: #8f58ff;
    font-size: 10px;
    white-space: nowrap;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
}

.pill-icon {
    font-size: 16px;
    line-height: 1;
}

.income-pill b {
    color: #8f58ff;
    font-size: 12px;
    font-weight: 800;
}

.limit-text b {
    font-size: 13px;
}

.progress-row {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 62px;
    gap: 12px;
    align-items: center;
    margin: 3px 0 20px;
}

.progress {
    height: 8px;
    border: 1px solid rgba(51, 60, 110, 0.35);
    border-radius: 99px;
    background: #081022;
    overflow: hidden;
}

.progress i {
    display: block;
    height: 100%;
    border-radius: 99px;
    background: linear-gradient(90deg, #8138ff, #00d5ff);
    box-shadow: 0 0 14px rgba(92, 88, 255, 0.85);
}

.percent {
    color: #8e50ff;
    font-size: 16px;
    font-weight: 800;
    text-align: right;
    white-space: nowrap;
}

.buy-wrap {
    text-align: right;
}

.buy-btn {
    height: 47px;
    padding: 0 34px;
    border: 0;
    border-radius: 10px;
    background: linear-gradient(90deg, #8f36ff, #272fff);
    box-shadow: 0 0 22px rgba(85, 53, 255, 0.35);
    color: #fff;
    font-size: 17px;
    font-weight: 800;
    cursor: pointer;
}

.buy-btn.disabled {
    background: rgba(255, 255, 255, 0.08);
    box-shadow: none;
    color: #8fa5c7;
    cursor: not-allowed;
}

.service-row {
    padding: 14px 5px;
    margin-bottom: 8px;
    display: grid;
    grid-template-columns: repeat(4, 1fr);
}

.service-item {
    border-right: 1px solid rgba(62, 77, 133, 0.32);
    color: #98a3bc;
    font-size: 10px;
    text-align: center;
}

.service-item:last-child {
    border-right: 0;
}

.service-item i {
    width: 34px;
    height: 34px;
    margin: 0 auto 5px;
    border: 1px solid rgba(112, 62, 255, 0.4);
    border-radius: 50%;
    background: rgba(73, 42, 153, 0.18);
    color: #8f4eff;
    display: grid;
    place-items: center;
    font-size: 19px;
    font-style: normal;
}

.service-item b {
    display: block;
    margin-bottom: 2px;
    color: #fff;
    font-size: 11px;
}

.bottom-nav {
    position: fixed;
    left: 50%;
    bottom: 0;
    z-index: 50;
    width: 100%;
    max-width: 430px;
    padding: 9px 8px max(9px, env(safe-area-inset-bottom));
    border-top: 1px solid rgba(255, 255, 255, 0.1);
    background: rgba(0, 0, 0, 0.92);
    backdrop-filter: blur(16px);
    transform: translateX(-50%);
    display: flex;
    align-items: center;
    justify-content: space-around;
    box-sizing: border-box;
}

.nav-item {
    min-width: 74px;
    padding: 7px 13px;
    color: #6b7280;
    text-decoration: none;
    display: flex;
    flex-direction: column;
    align-items: center;
}

.nav-item .van-icon {
    font-size: 23px;
}

.nav-item span {
    margin-top: 5px;
    font-size: 11px;
    line-height: 1.25;
    font-weight: 500;
}

.nav-item.active,
.nav-item.router-link-exact-active {
    color: #2d5bff;
}

.nav-item.active span,
.nav-item.router-link-exact-active span {
    font-weight: 700;
}

.service-float {
    position: fixed;
    right: max(18px, calc((100vw - 430px) / 2 + 18px));
    bottom: max(90px, calc(env(safe-area-inset-bottom) + 90px));
    z-index: 80;
    width: 54px;
    height: 54px;
    border: 1px solid rgba(156, 190, 255, 0.58);
    border-radius: 50%;
    background: linear-gradient(145deg, #337cff, #5939f3);
    color: #fff;
    box-shadow: 0 0 0 6px rgba(45, 91, 255, 0.1), 0 9px 24px rgba(21, 75, 255, 0.4);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
}

.service-float .van-icon {
    font-size: 28px;
}

/* ---------- 全屏协议弹窗 ---------- */
.agreement-popup {
    height: 100% !important;
    max-height: 100% !important;
    background: transparent;
}

.agreement-popup::after {
    display: none;
}

.agreement {
    display: flex;
    flex-direction: column;
    height: 100%;
    background:
        radial-gradient(circle at 85% 0%, rgba(38, 110, 220, 0.18), transparent 40%),
        linear-gradient(180deg, #0b1628 0%, #060b1a 100%);
    color: #fff;
}

.agreement-header {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 18px 16px 14px;
    border-bottom: 1px solid rgba(120, 160, 230, 0.1);
}

.agreement-header h2 {
    margin: 0;
    font-size: 18px;
    font-weight: 700;
    color: #eef3ff;
}

.agreement-close {
    width: 30px;
    height: 30px;
    padding: 0;
    border: 0;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.06);
    color: #cdd8ee;
    display: grid;
    place-items: center;
    cursor: pointer;
}

.agreement-close .van-icon {
    font-size: 16px;
}

.agreement-body {
    flex: 1 1 auto;
    overflow-y: auto;
    padding: 16px 16px 20px;
    -webkit-overflow-scrolling: touch;
}

.agree-section {
    margin-bottom: 18px;
}

.agree-section-title {
    display: flex;
    align-items: center;
    gap: 7px;
    margin: 0 0 12px;
    font-size: 14px;
    font-weight: 600;
    color: #6fb6ff;
}

.agree-section-title .van-icon {
    font-size: 16px;
    color: #4d8dff;
}

.agree-section-title .chev {
    margin-left: auto;
    font-size: 14px;
    color: #7d92b8;
}

.signature-toggle {
    cursor: pointer;
}

/* 基础信息 2 列 */
.info-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
}

.info-cell {
    padding: 12px 12px 11px;
    border: 1px solid rgba(70, 110, 190, 0.28);
    border-radius: 10px;
    background: rgba(12, 26, 50, 0.72);
    min-width: 0;
}

.info-cell label {
    display: block;
    margin-bottom: 7px;
    color: #8a9bb8;
    font-size: 11px;
    line-height: 1.3;
}

.info-cell b {
    display: block;
    font-size: 14px;
    line-height: 1.3;
    color: #fff;
    font-weight: 700;
    word-break: break-word;
}

.info-cell .product-name {
    font-size: 13px;
}

.accent-green {
    color: #2ee6a0 !important;
}

.accent-gold {
    color: #f0b53a !important;
}

.accent-blue {
    color: #4d8dff !important;
}

/* 合同条款 */
.terms-scroll {
    position: relative;
    max-height: 320px;
    padding: 14px 12px;
    border: 1px solid rgba(70, 110, 190, 0.28);
    border-radius: 10px;
    background: rgba(10, 22, 42, 0.78);
    overflow-y: auto;
}

.terms-text {
    position: relative;
    z-index: 1;
    padding-right: 78px;
    color: #c3cfe4;
    font-size: 12.5px;
    line-height: 1.75;
}

.terms-text p {
    margin: 0 0 10px;
}

.terms-text b {
    color: #e6edfb;
}

.terms-decor {
    position: sticky;
    float: right;
    top: 0;
    right: 0;
    width: 72px;
    height: 100%;
    min-height: 200px;
    pointer-events: none;
}

.td-avatar {
    position: absolute;
    top: 4px;
    right: 4px;
    width: 34px;
    height: 34px;
    border-radius: 50%;
    border: 2px solid rgba(255, 255, 255, 0.25);
    background: radial-gradient(circle at 35% 30%, #ffd0a0, #c45a8a 50%, #4a1a4a);
}

.td-wheel {
    position: absolute;
    top: 54px;
    right: 8px;
    width: 52px;
    height: 52px;
    border-radius: 50%;
    background: conic-gradient(
        #ffd25e 0 30deg, #ff6b6b 30deg 60deg,
        #4ecdc4 60deg 90deg, #6c5ce7 90deg 120deg,
        #ffd25e 120deg 150deg, #ff6b6b 150deg 180deg,
        #4ecdc4 180deg 210deg, #6c5ce7 210deg 240deg,
        #ffd25e 240deg 270deg, #ff6b6b 270deg 300deg,
        #4ecdc4 300deg 330deg, #6c5ce7 330deg 360deg
    );
    box-shadow: 0 0 12px rgba(108, 92, 231, 0.5);
}

.td-wheel::after {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    width: 10px;
    height: 10px;
    transform: translate(-50%, -50%);
    border-radius: 50%;
    background: #fff;
}

.td-chest {
    position: absolute;
    bottom: 8px;
    right: 6px;
    width: 46px;
    height: 38px;
    border-radius: 8px;
    background:
        linear-gradient(180deg, #ffcf5a, #b8860b);
    box-shadow: 0 0 12px rgba(240, 181, 58, 0.5);
}

.td-chest::before {
    content: "";
    position: absolute;
    left: 0;
    right: 0;
    top: 50%;
    height: 6px;
    transform: translateY(-50%);
    background: rgba(120, 70, 10, 0.7);
}

/* 签名区 */
.signature-body {
    padding: 4px 2px 0;
}

.signature-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 9px 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.06);
    color: #8a9bb8;
    font-size: 12.5px;
}

.signature-row b {
    color: #fff;
    font-weight: 600;
}

/* 底部 */
.agreement-footer {
    flex: 0 0 auto;
    padding: 12px 16px calc(14px + env(safe-area-inset-bottom));
    border-top: 1px solid rgba(120, 160, 230, 0.1);
    background: rgba(8, 15, 30, 0.9);
}

.accept-btn {
    width: 100%;
    height: 50px;
    border: 0;
    border-radius: 25px;
    background: linear-gradient(90deg, #2fd0e6, #27b6d6);
    box-shadow: 0 6px 18px rgba(45, 180, 220, 0.35);
    color: #04222b;
    font-size: 16px;
    font-weight: 800;
    cursor: pointer;
}

.accept-btn[disabled] {
    opacity: 0.6;
    cursor: not-allowed;
}

.footer-note {
    margin: 10px 0 0;
    text-align: center;
    color: #6f7f9e;
    font-size: 11px;
}

@media (max-width: 390px) {
    .page-shell {
        padding-left: 14px;
        padding-right: 14px;
    }

    .zone-grid {
        gap: 5px;
    }

    .zone h3 {
        font-size: 12px;
    }

    .product-card {
        --product-image-width: clamp(104px, 31%, 128px);
        --product-image-gap: 8px;
    }

    .metrics {
        width: 62%;
    }

    .money-card b {
        font-size: 17px;
    }

    .income-strip {
        gap: 5px;
    }

    .income-pill {
        padding: 0 4px;
        gap: 3px;
        font-size: 9px;
    }

    .income-pill b {
        font-size: 11px;
    }

    .limit-text b {
        font-size: 12px;
    }
}

@media (max-width: 340px) {
    .product-card {
        --product-image-width: 90px;
        --product-image-gap: 6px;
    }
}
</style>
